"use client";
import cookies from 'js-cookie';
import { useCallback, useEffect } from 'react';
import { ThemeName } from '@sravni/design-system-theme';
import { COOKIES } from '@sravni/utils/lib/consts';
import { postMessageType } from '@sravni/utils/lib/theme';
import { COOKIE_MAX_AGE } from '../providers/ThemeProvider/consts.js';
import { useTheme } from './useTheme.js';

const darkThemeExpression = '(prefers-color-scheme: dark)';
const setSystemCookie = (isDarkTheme) => {
    cookies.set(COOKIES.SYSTEM_THEME, isDarkTheme ? ThemeName.guinness : ThemeName.lager, {
        expires: new Date(new Date().getTime() + COOKIE_MAX_AGE),
        sameSite: 'Lax',
        path: '/',
    });
};
const isValidThemeName = (value) => [ThemeName.lager, ThemeName.guinness].includes(value);
/**
 * Следит за текущей темой браузера. При изменении вызывает изменение в провайдере темы(если пользователь принудительно не указал желаемую тему)
 * @returns null
 */
function useBrowserThemeDetector(props = {}) {
    const { enabled = true, onChange } = props;
    const [theme, setTheme] = useTheme();
    const updateThemeInThemeProvider = useCallback(() => {
        const userTheme = cookies.get(COOKIES.USER_THEME);
        const systemTheme = cookies.get(COOKIES.SYSTEM_THEME);
        if (userTheme && isValidThemeName(userTheme))
            return;
        if (!systemTheme || !isValidThemeName(systemTheme))
            return;
        if (theme === systemTheme)
            return;
        setTheme(systemTheme, false);
        onChange?.(systemTheme);
    }, [theme, setTheme, onChange]);
    const postMessageHandler = useCallback((event) => {
        const message = event.data;
        const { type, value } = message || {};
        if (typeof type === 'undefined' || type !== postMessageType)
            return;
        if (typeof value === 'undefined' || !isValidThemeName(value))
            return;
        if (theme === value)
            return;
        setTheme(value, false);
        onChange?.(value);
    }, [theme, setTheme, onChange]);
    // Проверка сразу после mount
    useEffect(() => {
        if (!enabled)
            return;
        if (!window.matchMedia)
            return;
        const isDarkTheme = window.matchMedia(darkThemeExpression).matches;
        setSystemCookie(isDarkTheme);
        updateThemeInThemeProvider();
    }, [enabled, updateThemeInThemeProvider]);
    // Подписываемся на изменения темы браузера
    useEffect(() => {
        if (!enabled)
            return;
        if (!window.matchMedia)
            return;
        window.matchMedia(darkThemeExpression).addEventListener('change', ({ matches: isDarkTheme }) => {
            setSystemCookie(isDarkTheme);
            updateThemeInThemeProvider();
        });
    }, [enabled, updateThemeInThemeProvider]);
    // Подписываемся на postMessage с новой темой(используется в iframe)
    useEffect(() => {
        if (!enabled)
            return;
        if (!window.addEventListener)
            return;
        window.addEventListener('message', postMessageHandler);
        return () => {
            window.removeEventListener('message', postMessageHandler);
        };
    }, [enabled, postMessageHandler, updateThemeInThemeProvider]);
    return null;
}

export { useBrowserThemeDetector };
//# sourceMappingURL=useBrowserThemeDetector.js.map
